import { supabase } from '../supabaseClient';
import type { Filters } from './consts';
import { minPostedAtForDateRange } from './dateRange';
import { docToRental, pricePerMonthInBaseCurrency, sortRentals, canonicalLikedIds, type Rental, type RentalDoc } from './rentalTransforms';

export { docToRental, pricePerMonthInBaseCurrency, sortRentals, canonicalLikedIds };
export type { Rental };

export const RENTAL_LIST_COLUMNS = 'id,message_id,title,property_type,location,bedrooms,bathrooms,price_amount,price_currency,rental_period,media_urls,posted_at_source,source_url,source_channel_name,source_channel_id,source,slug,topic_id';
export const RENTAL_DETAIL_COLUMNS = 'id,message_id,title,property_type,location,bedrooms,bathrooms,price_amount,price_currency,rental_period,media_urls,amenities,contact_type,contact_url,contact_name,raw_description,posted_at_source,source_url,source_channel_name,source_channel_id,source,slug,topic_id';
export const RENTAL_COLUMNS = RENTAL_DETAIL_COLUMNS;
const RENTAL_FETCH_PAGE_SIZE = 1000;
export const RENTAL_LIST_PAGE_SIZE = 100;
const RENTAL_DETAIL_BATCH_SIZE = 500;

export interface RentalPage {
    rentals: Rental[];
    hasMore: boolean;
    totalCount: number | null;
}

interface RentalQueryOptions {
    offset?: number;
    pageSize?: number;
    locationNames?: string[] | null;
    minPostedAt?: Date;
}

const postgrestQuotedValue = (value: string) => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

export const fetchRentalsById = async (rentalIds: string[]) => {
    const resolved: { requestedId: string; rental: Rental }[] = [];
    for (let from = 0; from < rentalIds.length; from += RENTAL_DETAIL_BATCH_SIZE) {
        const { data, error } = await supabase
            .from('rental_aliases')
            .select(`requested_id,${RENTAL_COLUMNS}`)
            .in('requested_id', rentalIds.slice(from, from + RENTAL_DETAIL_BATCH_SIZE));
        if (error) throw error;
        for (const row of (data || []) as unknown as (RentalDoc & { requested_id: string })[]) {
            const { requested_id, ...doc } = row;
            resolved.push({ requestedId: requested_id, rental: docToRental(doc) });
        }
    }
    return resolved;
}

export const fetchRentalDetails = async (rentalIds: string[]): Promise<Rental[]> => {
    const allData: RentalDoc[] = [];
    for (let from = 0; from < rentalIds.length; from += RENTAL_DETAIL_BATCH_SIZE) {
        const ids = rentalIds.slice(from, from + RENTAL_DETAIL_BATCH_SIZE);
        const { data, error } = await supabase
            .from('canonical_rentals')
            .select(RENTAL_DETAIL_COLUMNS)
            .in('id', ids);

        if (error) {
            console.error('Error fetching rental details:', error);
            throw error;
        }

        allData.push(...((data || []) as unknown as RentalDoc[]));
    }

    return allData.map(docToRental);
}

const buildRentalsQuery = (
    filters: Filters,
    from: number,
    to: number,
    {
        includeCount = false,
        locationNames,
        minPostedAt = minPostedAtForDateRange(filters.dateRange),
    }: { includeCount?: boolean; locationNames?: string[] | null; minPostedAt?: Date } = {},
) => {
    let query = supabase
        .from('canonical_rentals')
        .select(RENTAL_LIST_COLUMNS, includeCount ? { count: 'exact' } : undefined)
        .order('posted_at_source', { ascending: false })
        .order('id', { ascending: true })
        .range(from, to);

    if (filters.min_bedrooms) {
        query = query.gte('bedrooms', filters.min_bedrooms);
    }
    if (locationNames?.length) {
        const locationFilter = locationNames
            .map(locationName => `location.ilike.${postgrestQuotedValue(locationName)}`)
            .join(',');
        query = query.or(locationFilter);
    }
    // Note: price filtering is done client-side (in useRentals) to correctly
    // handle listings priced in non-IDR currencies via exchange rate conversion.
    if (minPostedAt) {
        query = query.gte('posted_at_source', minPostedAt.toISOString());
    }

    return query;
};

export const fetchRentalPage = async (
    filters: Filters,
    { offset = 0, pageSize = RENTAL_LIST_PAGE_SIZE, locationNames, minPostedAt }: RentalQueryOptions = {},
): Promise<RentalPage> => {
    if (locationNames && locationNames.length === 0) {
        return { rentals: [], hasMore: false, totalCount: 0 };
    }

    const { data, error, count } = await buildRentalsQuery(
        filters,
        offset,
        offset + pageSize - 1,
        { includeCount: true, locationNames, minPostedAt },
    );

    if (error) {
        console.error('Error fetching rentals:', error);
        throw error;
    }

    const page = ((data || []) as unknown as RentalDoc[]).slice(0, pageSize);
    return {
        rentals: sortRentals(page.map(docToRental), filters.sort),
        hasMore: count === null ? (data || []).length === pageSize : offset + pageSize < count,
        totalCount: count,
    };
};

export const fetchRentals = async (
    filters: Filters,
    { locationNames, minPostedAt }: Pick<RentalQueryOptions, 'locationNames' | 'minPostedAt'> = {},
): Promise<Rental[]> => {
    if (locationNames && locationNames.length === 0) return [];

    const allData: RentalDoc[] = [];
    for (let from = 0; ; from += RENTAL_FETCH_PAGE_SIZE) {
        const to = from + RENTAL_FETCH_PAGE_SIZE - 1;
        const { data, error } = await buildRentalsQuery(filters, from, to, { locationNames, minPostedAt });

        if (error) {
            console.error('Error fetching rentals:', error);
            throw error;
        }

        const page = (data || []) as unknown as RentalDoc[];
        allData.push(...page);
        if (page.length < RENTAL_FETCH_PAGE_SIZE) break;
    }
    const rentals = allData.map(docToRental);
    return sortRentals(rentals, filters.sort);
};
